import api from "./client";
import type { PaginatedResponse } from "@/types/common";
import type {
  AgingReport,
  Invoice,
  InvoiceStatus,
  LineItem,
  Payment,
  PaymentMethod,
  Reminder,
  ReminderKind,
} from "@/types/invoice";

export async function listInvoices(
  page = 1,
  perPage = 20,
  employeeId?: string,
  status?: InvoiceStatus,
): Promise<PaginatedResponse<Invoice>> {
  const params: Record<string, string | number> = { page, per_page: perPage };
  if (employeeId) params.employee_id = employeeId;
  if (status) params.status = status;
  const { data } = await api.get("/invoices", { params });
  return data;
}

export async function getInvoice(id: string): Promise<Invoice> {
  const { data } = await api.get(`/invoices/${id}`);
  return data;
}

export interface CreateInvoiceBody {
  /** Optional internal assignee (nullable since 046). */
  employee_id?: string | null;
  /** Optional FK into the per-org clients address book. */
  client_id?: string | null;
  /** Free-text override; wins over the saved client's email at send time. */
  client_email?: string | null;
  period_start: string;
  period_end: string;
  line_items: LineItem[];
  tax_rate?: number;
  due_date?: string;
  notes?: string;
  terms?: string;
  currency?: string;
}

export async function createInvoice(body: CreateInvoiceBody) {
  // Strip nullable-but-empty fields so we don't send "" where the
  // backend expects either a valid value or omission.
  const payload: Record<string, unknown> = {
    period_start: body.period_start,
    period_end: body.period_end,
    line_items: body.line_items,
    tax_rate: body.tax_rate ?? 0,
  };
  if (body.employee_id) payload.employee_id = body.employee_id;
  if (body.client_id) payload.client_id = body.client_id;
  if (body.client_email) payload.client_email = body.client_email;
  if (body.due_date) payload.due_date = body.due_date;
  if (body.notes) payload.notes = body.notes;
  if (body.terms) payload.terms = body.terms;
  if (body.currency) payload.currency = body.currency;
  const { data } = await api.post("/invoices", payload);
  return data as { id: string; invoice_number: string; currency: string };
}

export async function generateFromTimesheets(
  employeeId: string,
  periodStart: string,
  periodEnd: string,
  hourlyRate: number,
  dueDate?: string,
  notes?: string,
) {
  const { data } = await api.post("/invoices/generate", {
    employee_id: employeeId,
    period_start: periodStart,
    period_end: periodEnd,
    hourly_rate: hourlyRate,
    due_date: dueDate,
    notes,
  });
  return data;
}

export async function issueInvoice(id: string) {
  const { data } = await api.post(`/invoices/${id}/issue`);
  return data as { message: string; issued_at: string; status: InvoiceStatus };
}

export interface SendInvoiceResult {
  message: string;
  sent_at: string;
  status: InvoiceStatus;
  sent_to_email?: string | null;
}

/**
 * Send the invoice email. Post-046 the backend no longer silently
 * swallows delivery failures — if the provider rejects the send, this
 * call rejects with a 502 whose ``detail`` carries the actual error +
 * the ``sent_at`` timestamp (the FSM still transitions to SENT). The
 * caller is expected to surface that detail to the user.
 */
export async function sendInvoice(
  id: string,
  toEmail?: string,
  ccAssignedEmployee = false,
): Promise<SendInvoiceResult> {
  const body: Record<string, unknown> = {};
  if (toEmail) body.to_email = toEmail;
  if (ccAssignedEmployee) body.cc_assigned_employee = true;
  const { data } = await api.post(`/invoices/${id}/send`, body);
  return data;
}

export async function markInvoicePaid(id: string) {
  const { data } = await api.post(`/invoices/${id}/mark-paid`);
  return data as { message: string; paid_at: string; status: InvoiceStatus };
}

export async function voidInvoice(id: string, reason: string) {
  const { data } = await api.post(`/invoices/${id}/void`, { reason });
  return data as { message: string; voided_at: string; status: InvoiceStatus };
}

export interface RecordPaymentBody {
  amount: number;
  method: PaymentMethod;
  reference?: string;
  received_at?: string;
  notes?: string;
}

export async function recordPayment(
  id: string,
  body: RecordPaymentBody,
): Promise<Payment> {
  const { data } = await api.post(`/invoices/${id}/payments`, body);
  return data;
}

export async function listPayments(id: string): Promise<Payment[]> {
  const { data } = await api.get(`/invoices/${id}/payments`);
  return data;
}

export async function sendReminder(
  id: string,
  kind: ReminderKind,
  toEmail?: string,
): Promise<Reminder> {
  const body: Record<string, string> = { kind };
  if (toEmail) body.to_email = toEmail;
  const { data } = await api.post(`/invoices/${id}/remind`, body);
  return data;
}

export async function listReminders(id: string): Promise<Reminder[]> {
  const { data } = await api.get(`/invoices/${id}/reminders`);
  return data;
}

export async function downloadPdf(id: string): Promise<Blob> {
  const { data } = await api.get(`/invoices/${id}/pdf`, {
    responseType: "blob",
  });
  return data;
}

export async function listOverdue(asOf?: string): Promise<Invoice[]> {
  const params: Record<string, string> = {};
  if (asOf) params.as_of = asOf;
  const { data } = await api.get("/invoices/overdue", { params });
  return data;
}

export async function getAgingReport(asOf?: string): Promise<AgingReport> {
  const params: Record<string, string> = {};
  if (asOf) params.as_of = asOf;
  const { data } = await api.get("/invoices/aging-report", { params });
  return data;
}

export async function exportCsv(status?: InvoiceStatus): Promise<Blob> {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  const { data } = await api.get("/invoices/export.csv", {
    params,
    responseType: "blob",
  });
  return data;
}
